import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import { adminUpdateOrder, createElderServiceEvent, updateElderServiceEvent } from 'graphql/mutations';
import { request } from 'utilities/graph';
import { toastr } from 'react-redux-toastr';
import cache from 'utilities/cache';
import { asyncListAll } from 'utilities/graph';
import { getOrdersByElderByDate } from './queries';
import { elderServiceEventConfig } from './helpers';
import { sortBy } from 'utilities/sorting';
import OrderCard from './OrderCard';
import elderDeactivationReason from '@silvergatedelivery/constants/src/elderDeactivationReason';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 600,
  },
  dateRange: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  formSection: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  summary: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning[50],
    borderRadius: theme.spacing(1),
  },
}));

export default function ElderStopServiceDialog({
  open,
  onClose,
  clientId,
  elderId,
  dateRange,
  onSuccess,
  editingEvent = null,
}) {
  const classes = useStyles();
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState();
  const config = elderServiceEventConfig.serviceStop;

  const isEditing = !!editingEvent;

  useEffect(() => {
    if (editingEvent) {
      setReason(editingEvent.description || '');
      setNote(editingEvent.note || '');
      setCurrentDate(moment(editingEvent.startedAt).format('YYYY-MM-DD'));
    } else {
      setReason('');
      setNote('');
      if (dateRange?.start) {
        setCurrentDate(moment(dateRange.start).format('YYYY-MM-DD'));
      } else {
        setCurrentDate();
      }
    }
  }, [editingEvent, dateRange]);

  useEffect(() => {
    if (!open || !elderId || !currentDate) return;

    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        let allOrders = [];

        if (isEditing) {
          const originalOrders = (await asyncListAll(getOrdersByElderByDate, {
            elderId,
            date: {
              gt: moment(dateRange.start).subtract(1, 'day').format('YYYY-MM-DD'),
            },
            filter: {
              and: [
                {
                  status: {
                    ne: 'completed',
                  },
                },
                {
                  status: {
                    ne: 'delivered',
                  },
                },
              ],
            },
          })).filter((order) => {
            return order.status !== 'cancelled' || (order.status === 'cancelled' && order.cancellationReason === config.cancellationReason);
          });

          const currentOrders = (await asyncListAll(getOrdersByElderByDate, {
            elderId,
            date: {
              gt: moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD'),
            },
            filter: {
              and: [
                {
                  status: {
                    ne: 'completed',
                  },
                },
                {
                  status: {
                    ne: 'delivered',
                  },
                },
              ],
            },
          })).filter((order) => {
            return order.status !== 'cancelled' || (order.status === 'cancelled' && order.cancellationReason === config.cancellationReason);
          });

          const orderMap = new Map();
          [...originalOrders, ...currentOrders].forEach((order) => {
            if (!orderMap.has(order.id)) {
              orderMap.set(order.id, order);
            }
          });
          allOrders = Array.from(orderMap.values());
        } else {
          allOrders = await asyncListAll(getOrdersByElderByDate, {
            elderId,
            date: {
              gt: moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD'),
            },
            filter: {
              and: [
                {
                  status: {
                    ne: 'completed',
                  },
                },
                {
                  status: {
                    ne: 'delivered',
                  },
                },
                {
                  status: {
                    ne: 'cancelled',
                  },
                },
              ],
            },
          });
        }
        setOrders(allOrders.sort(sortBy('date')));

        const newSelectedOrders = new Set();
        const currentStart = moment(currentDate).format('YYYY-MM-DD');
        allOrders.forEach((order) => {
          const orderDate = moment(order.date).format('YYYY-MM-DD');
          const isInCurrentRange = orderDate >= currentStart;
          if (isInCurrentRange) {
            newSelectedOrders.add(order.id);
          }
        });
        setSelectedOrders(newSelectedOrders);
      } catch (error) {
        console.error('查詢訂單失敗:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [open, elderId, currentDate, isEditing, dateRange]);

  const toggleOrderSelection = (orderId) => {
    const newSelectedOrders = new Set(selectedOrders);
    if (newSelectedOrders.has(orderId)) {
      newSelectedOrders.delete(orderId);
    } else {
      newSelectedOrders.add(orderId);
    }
    setSelectedOrders(newSelectedOrders);
  };

  const toggleAllOrders = (checked) => {
    if (checked) {
      setSelectedOrders(new Set(orders.map((order) => order.id)));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSubmit = async () => {
    if (!reason) {
      toastr.warning(`請輸入${config.reasonLabel}`);
      return;
    }

    if (!currentDate) {
      toastr.warning(`請選擇${config.dateLabel}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');

      if (isEditing) {
        const orderOperationsData = [];
        const toCancelOrders = orders.filter((order) => {
          const isSelected = selectedOrders.has(order.id);
          return isSelected && order.status !== 'cancelled';
        });

        const deactivationReason = elderDeactivationReason.find(({ value }) => value === reason);
        orderOperationsData.push(...toCancelOrders.map((order) => (
          {
            id: order.id,
            restaurantId: order.restaurantId,
            deliveryBy: order.deliveryBy,
            status: 'cancelled',
            tier: order.tier,
            cancellationReason: config.cancellationReason,
            cancellationNote: deactivationReason?.label || config.defaultNote,
          }
        )));

        const toResetOrders = orders.filter((order) => {
          const isSelected = selectedOrders.has(order.id);
          return !isSelected && order.status === 'cancelled';
        });

        orderOperationsData.push(...toResetOrders.map((order) => (
          {
            id: order.id,
            restaurantId: order.restaurantId,
            deliveryBy: order.deliveryBy,
            status: order.deliveryStaffId ? 'ready' : 'waitingForDeliveryStaff',
            tier: order.tier,
            cancellationReason: undefined,
            cancellationNote: undefined,
          }
        )));

        if (orderOperationsData.length > 0) {
          await request(adminUpdateOrder, { input: { orders: orderOperationsData } });
        }
        await request(updateElderServiceEvent, {
          input: {
            id: editingEvent.id,
            startedAt: moment(currentDate).toISOString(),
            description: reason,
            note: note,
            updatedAt: now,
            updatedBy: userName,
          },
        });
      } else {
        const ordersToCancel = orders.filter((order) => {
          const isSelected = selectedOrders.has(order.id);
          return isSelected && order.status !== 'cancelled';
        });

        const deactivationReason = elderDeactivationReason.find(({ value }) => value === reason);
        const orderOperationsData = ordersToCancel.map((order) => (
          {
            id: order.id,
            restaurantId: order.restaurantId,
            deliveryBy: order.deliveryBy,
            status: 'cancelled',
            tier: order.tier,
            cancellationReason: config.cancellationReason,
            cancellationNote: deactivationReason?.label || config.defaultNote,
          }
        ));
        if (orderOperationsData.length > 0) {
          await request(adminUpdateOrder, { input: { orders: orderOperationsData } });
        }
        await request(createElderServiceEvent, {
          input: {
            clientId: clientId,
            elderId,
            type: config.eventType,
            startedAt: moment(currentDate).toISOString(),
            description: reason,
            note: note,
            status: '使用中',
            createdAt: now,
            createdBy: userName,
            updatedAt: now,
            updatedBy: userName,
          },
        });
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error(`${isEditing ? '更新' : '建立'}${config.title}失敗:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOrders([]);
      setSelectedOrders(new Set());
      setReason('');
      setNote('');
      setCurrentDate();
      onClose();
    }
  };

  const selectedOrdersList = Array.from(selectedOrders);
  const currentRangeOrders = orders.filter((order) => {
    const orderDate = moment(order.date).format('YYYY-MM-DD');
    const currentStart = moment(currentDate).format('YYYY-MM-DD');
    return orderDate >= currentStart;
  });

  const outsideRangeOrders = orders.filter((order) => {
    const orderDate = moment(order.date).format('YYYY-MM-DD');
    const currentStart = moment(currentDate).format('YYYY-MM-DD');
    return orderDate < currentStart;
  });

  const willCancelCount = orders.filter((order) => {
    const isSelected = selectedOrders.has(order.id);
    return isSelected && order.status !== 'cancelled';
  }).length;

  const willRestoreCount = orders.filter((order) => {
    const isSelected = selectedOrders.has(order.id);
    return !isSelected && order.status === 'cancelled';
  }).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        {isEditing ? `修改${config.title}` : config.title}
      </DialogTitle>

      <DialogContent>
        <div className={classes.dateRange}>
          <Typography variant="h6" gutterBottom>
            {config.dateLabel}
          </Typography>
          {isEditing ? (
            <TextField
              label={config.dateLabel}
              type="date"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          ) : (
            <Typography variant="body1">
              {moment(dateRange?.start).format('YYYY-MM-DD')} 起
            </Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            將停止從此日期開始的所有訂單
          </Typography>
        </div>

        <div className={classes.formSection}>
          <Typography variant="h6" gutterBottom>
            {config.reasonLabel}*
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ marginBottom: 16 }}
            inputProps={{ 'aria-label': '選擇原因' }}
          >
            <MenuItem value="" disabled>
              請選擇{config.reasonLabel}
            </MenuItem>
            {elderDeactivationReason.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="h6" gutterBottom>
            {config.noteLabel}
          </Typography>
          <TextField
            fullWidth
            placeholder={config.notePlaceholder}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {!isEditing && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Typography variant="h6">
                即將取消的訂單 ({orders.length})
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOrders.size === orders.length && orders.length > 0}
                    indeterminate={selectedOrders.size > 0 && selectedOrders.size < orders.length}
                    onChange={(e) => toggleAllOrders(e.target.checked)}
                    color="primary"
                  />
                }
                label="全選"
              />
            </div>

            {isLoading ? (
              <div className={classes.loadingContainer}>
                <Typography>載入訂單中...</Typography>
              </div>
            ) : orders.length === 0 ? (
              <Alert severity="info">
                此日期範圍內沒有訂單
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isSelected={selectedOrders.has(order.id)}
                    onToggleSelection={toggleOrderSelection}
                  />
                ))}
              </Grid>
            )}

            {selectedOrdersList.length > 0 && (
              <div className={classes.summary}>
                <Alert severity="info">
                  將取消 {selectedOrdersList.length} 張訂單
                </Alert>
              </div>
            )}
          </>
        )}

        {isEditing && (
          <>
            <Typography variant="h6" gutterBottom>
              訂單列表 ({orders.length})
            </Typography>
            {isLoading ? (
              <div className={classes.loadingContainer}>
                <Typography>載入訂單中...</Typography>
              </div>
            ) : orders.length === 0 ? (
              <Alert severity="info">
                此日期範圍內沒有訂單
              </Alert>
            ) : (
              <div>
                <Typography variant="h6" gutterBottom style={{ marginTop: 16, marginBottom: 8 }}>
                  目前停止服務日期範圍的訂單 ({currentRangeOrders.length})
                </Typography>
                {currentRangeOrders.length > 0 && (
                  <Grid container spacing={2} style={{ marginBottom: 24 }}>
                    {currentRangeOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        isSelected={selectedOrders.has(order.id)}
                        onToggleSelection={toggleOrderSelection}
                      />
                    ))}
                  </Grid>
                )}
                {outsideRangeOrders.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom style={{ marginTop: 16, marginBottom: 8 }}>
                      其他日期的訂單 ({outsideRangeOrders.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {outsideRangeOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          isSelected={selectedOrders.has(order.id)}
                          onToggleSelection={toggleOrderSelection}
                        />
                      ))}
                    </Grid>
                  </>
                )}
              </div>
            )}

            <div className={classes.summary}>
              <Alert severity="info">
                將取消 {willCancelCount} 張訂單，重置 {willRestoreCount} 張已取消的訂單
              </Alert>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !reason || !currentDate}
        >
          {isSubmitting ? '處理中...' : (isEditing ? '更新' : '送出')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ElderStopServiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  elderId: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  }),
  onSuccess: PropTypes.func,
  editingEvent: PropTypes.object,
};
